import {
  resolveRangePreset,
  type DateRangePresetId,
} from '@w7/ui'
import {
  PREVIOUS_RANGE_MAX_DAYS,
  shouldUseTickerBq,
} from '@/features/bet-ticker/domain'
import type {
  SurveillanceCurrency,
  SurveillanceGraphFilter,
  SurveillanceMode,
} from './types'

export const CURRENCY_OPTIONS: SurveillanceCurrency[] = [
  'All',
  'INR',
  'POINTS',
  'POINT@1',
]

/** Same presets as Bet Ticker Previous `DateRangePicker`. */
export const SURVEILLANCE_DATE_PRESETS: DateRangePresetId[] = [
  'today',
  'yesterday',
  'this-week',
  'last-week',
  'last-7',
  'last-30',
  'custom',
]

/** Same span cap as Bet Ticker Previous. */
export const SURVEILLANCE_RANGE_MAX_DAYS = PREVIOUS_RANGE_MAX_DAYS

/** Lab: Search needs sub game + event + market. */
export function canSearchSurveillance(filter: {
  eventType: string
  eventId: string
  marketId: string
}): boolean {
  return Boolean(filter.eventType && filter.eventId && filter.marketId)
}

/**
 * Lab stake rule: when both from and to are set, to must be ≥ from.
 * Returns an operator-facing message or null when valid.
 */
export function validateStakeRange(
  fromStake: number | null,
  toStake: number | null,
): string | null {
  if (
    fromStake != null &&
    toStake != null &&
    Number.isFinite(fromStake) &&
    Number.isFinite(toStake) &&
    toStake < fromStake
  ) {
    return 'Stake From amount should be less than or equal to Stake To amount'
  }
  return null
}

/**
 * Bet Ticker Previous 7-day switch for Surveillance:
 * within 7 calendar days ending today → Postgres live APIs;
 * otherwise → BQ (`bqGetBetsForSurv` + archive pickers).
 */
export function shouldUseSurveillanceBq(
  from: string,
  to: string,
  now: Date = new Date(),
): boolean {
  return shouldUseTickerBq(from, to, now)
}

export function surveillanceModeForRange(
  from: string,
  to: string,
  now: Date = new Date(),
): SurveillanceMode {
  return shouldUseSurveillanceBq(from, to, now) ? 'archive' : 'live'
}

/** Default window: today (same as Bet Ticker Previous). */
export function defaultLiveDateRange(now: Date = new Date()): {
  fromDate: string
  toDate: string
} {
  const range = resolveRangePreset('today', now)
  return { fromDate: range!.from, toDate: range!.to }
}

/** Merged Surveillance page filter — always has a date range; mode follows 7-day BQ switch. */
export function emptyGraphFilter(now: Date = new Date()): SurveillanceGraphFilter {
  const { fromDate, toDate } = defaultLiveDateRange(now)
  return {
    mode: surveillanceModeForRange(fromDate, toDate, now),
    eventType: '',
    eventId: '',
    marketId: '',
    playerNames: [],
    fromStake: null,
    toStake: null,
    currencyType: 'All',
    fromDate,
    toDate,
  }
}

export function isoToday(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function surveillanceDatePresetOf(
  from: string,
  to: string,
  today: Date = new Date(),
): DateRangePresetId {
  for (const id of SURVEILLANCE_DATE_PRESETS) {
    const resolved = resolveRangePreset(id, today)
    if (resolved && resolved.from === from && resolved.to === to) return id
  }
  return 'custom'
}

/** Filter bets by optional player / stake / currency (client-side for static). */
export function filterBetsForDisplay<
  T extends {
    u: string
    s: number
    currencyType?: string
  },
>(
  bets: T[],
  filter: Pick<
    SurveillanceGraphFilter,
    'playerNames' | 'fromStake' | 'toStake' | 'currencyType'
  >,
): T[] {
  return bets.filter((bet) => {
    if (filter.playerNames.length && !filter.playerNames.includes(bet.u)) {
      return false
    }
    if (filter.fromStake != null && bet.s < filter.fromStake) return false
    if (filter.toStake != null && bet.s > filter.toStake) return false
    if (
      filter.currencyType &&
      filter.currencyType !== 'All' &&
      bet.currencyType &&
      bet.currencyType !== filter.currencyType
    ) {
      return false
    }
    return true
  })
}
